import usePagination from '@/hooks/usePagination';
import {
  ContentEntity,
  ContentFilters,
  GeneratedByEnum,
  Maybe,
  ResourceStatusEnum,
  ResourceTypeEnum,
  SortType,
} from '@/types/schema';

import { useLearningContentsQuery } from '../../graphql/queries/generated/contents';

interface Params {
  searchString?: string;
  ids?: string[];
  idsToExclude?: string[];
  types?: ResourceTypeEnum[];
  pause?: boolean;
  generatedBy?: Maybe<GeneratedByEnum>;
  excludeReceivedContents?: boolean;
  shareable?: boolean;
  otherFilters?: ContentFilters;
  sort?: Maybe<SortType[]>;
  sourceLanguageContent?: boolean;
}

export default function useContents({
  searchString,
  ids,
  idsToExclude,
  types,
  pause,
  generatedBy,
  excludeReceivedContents,
  shareable,
  otherFilters,
  sort,
  sourceLanguageContent,
}: Params) {
  const { data, error, fetchMore, isFetching, isFetchingMore, refetch } = usePagination(
    useLearningContentsQuery,
    {
      filters: {
        ids,
        types,
        generatedBy,
        name: searchString,
        status: [ResourceStatusEnum.Published, ResourceStatusEnum.UnpublishedChanges],
        excludeReceivedContents,
        ...(sourceLanguageContent === false ? {} : { sourceLanguageContent: true }),
        shareable,
        ...otherFilters,
      },
      sort,
    },
    {
      pageSize: ids?.length ? ids.length : undefined,
      edgeKey: 'contents',
      pause: pause || (ids?.length === 0 && searchString === undefined),
    },
  );

  const contents =
    data.reduce((acc, content) => {
      if (!idsToExclude?.includes((content as ContentEntity).id)) {
        acc.push(content as ContentEntity);
      }
      return acc;
    }, [] as ContentEntity[]) ?? [];

  return {
    contents,
    error,
    fetchMore,
    isFetching,
    isFetchingMore,
    refetch,
  };
}
